import { InputAdornment, Stack, Typography } from '@mui/material';
import { compact } from 'lodash';
import { useMemo } from 'react';

import { AccessCard } from '@/components/cards/AccessCard/AccessCard';
import { FormAwareRadioGroup } from '@/components/form/formAwareInputs/FormAwareRadioGroup';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { ModalFormLayout } from '@/components/form/layout/ModalFormLayout';
import { Mail01Icon } from '@/components/icons/Mail01Icon';
import { Card } from '@/components/layout/Card/Card';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFormContext } from '@/components/react-hook-form';
import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';
import { ClientsTypeahead } from '@/modules/household/components/ClientsTypeahead/ClientsTypeahead';
import { diagnostics } from '@/utils/diagnostics';

import {
  CollaboratorFormShape,
  ROLE_OPTION_ITEMS,
} from './CollaboratorModal.constants';
import { useGetCollaboratorRolesQuery } from './graphql/GetCollaboratorRoles.generated';

interface CollaboratorFormProps {
  variant: 'create' | 'edit';
}

export function getRoleDetails(roleDisplay: string) {
  switch (roleDisplay) {
    case 'Collaborator edit access':
      return {
        label: 'Edit access',
        helpText:
          'Household edit access; can add and edit individuals and entities',
      };
    case 'Collaborator view access':
      return {
        label: 'Limited access',
        helpText: 'Household view-only access; can upload documents',
      };
    default:
      // This is unexpected, but this pattern is somewhat brittle, so we'll
      // log an error and return a disabled option
      diagnostics.error('Unknown role display', undefined, { roleDisplay });
      return {
        disabled: true,
        label: 'Unknown',
        helpText: 'Unknown access level',
      };
  }
}

export function CollaboratorForm({ variant }: CollaboratorFormProps) {
  const { control } = useFormContext<CollaboratorFormShape>();
  const { data: rolesData, loading: rolesLoading } =
    useGetCollaboratorRolesQuery();
  const canViewHouseholds = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_VIEW_HOUSEHOLDS,
    { detailed: false }
  );

  const roleOptions = useMemo(() => {
    return compact(rolesData?.collaboratorRoles).map((role) => ({
      value: role.id,
      ...getRoleDetails(role.displayName),
    }));
  }, [rolesData]);

  return (
    <ModalFormLayout>
      <FormLayoutRow>
        <FormLayoutItem>
          <Card variant="filled" sx={{ p: 2 }}>
            <Stack spacing={2}>
              <FormAwareSelectInput<CollaboratorFormShape>
                control={control}
                label="Type"
                // TODO (LUM-1552): Typed as string, fix to be more specific
                fieldName="role"
                options={ROLE_OPTION_ITEMS}
                required
              />
              {roleOptions.length > 0 && (
                <FormAwareRadioGroup<CollaboratorFormShape>
                  control={control}
                  label="Access level"
                  fieldName="roleId"
                  options={roleOptions}
                  row={false}
                  columnSpacing={1}
                  required
                />
              )}
              {rolesLoading && (
                <Typography variant="body-s">Loading roles...</Typography>
              )}
            </Stack>
          </Card>
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow>
        <FormLayoutItem width={6}>
          <FormAwareTextInput<CollaboratorFormShape>
            control={control}
            fieldName="firstName"
            label="First name"
            required
          />
        </FormLayoutItem>
        <FormLayoutItem width={6}>
          <FormAwareTextInput<CollaboratorFormShape>
            control={control}
            fieldName="lastName"
            label="Last name"
            required
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow>
        <FormLayoutItem>
          <FormAwareTextInput<CollaboratorFormShape>
            control={control}
            // don't allow changing the email address for existing collaborators; it could have unintended consequences
            // and isn't supported by the backend
            disabled={variant === 'edit'}
            type="email"
            fieldName="email"
            label="Email"
            startAdornment={
              <InputAdornment position="start">
                <Mail01Icon size={16} />
              </InputAdornment>
            }
            required
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow>
        <FormLayoutItem width={6}>
          <FormAwareTextInput<CollaboratorFormShape>
            control={control}
            fieldName="title"
            label="Title"
          />
        </FormLayoutItem>
        <FormLayoutItem width={6}>
          <FormAwareTextInput<CollaboratorFormShape>
            control={control}
            fieldName="organization"
            label="Organization"
          />
        </FormLayoutItem>
      </FormLayoutRow>
      {canViewHouseholds ? (
        <FormLayoutRow>
          <FormLayoutItem>
            <AccessCard
              heading="Client access"
              subheading="Select individual clients that this collaborator can see."
            >
              <ClientsTypeahead<CollaboratorFormShape>
                control={control}
                fieldName="associatedClientIds"
                label="Individual clients"
              />
            </AccessCard>
          </FormLayoutItem>
        </FormLayoutRow>
      ) : null}
    </ModalFormLayout>
  );
}
